import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { PackageBoundProduct } from './usePackageProduct'

export type PackageTagShopProduct = {
  id: string
  shopify_product_id: string
  sku: string
  title: string
  images: string[]
  variants: any[]
  selected_variant: {
    id: string
    sku: string
    title: string
    image: string | null
  } | null
}

export type PackageListItem = {
  id: string
  name: string
  company_id: string | null
  created_at: string
  updated_at: string | null
  /** VERA-790: when the package first reached capacity (stamped during provisioning). Null until filled. */
  filled_at: string | null
  quantity: number
  tag_shop_product: PackageTagShopProduct | null
  /** VERA-790 Phase 1 "package handle": chip UID of the plain NDEF URL tag on the box, if encoded. */
  handle_tag_uid: string | null
  /**
   * VRG-282 — the BRAND's product this box is pre-assigned to. Not to be confused with
   * `tag_shop_product`, which is the tag SKU the box was purchased as. Null until bound.
   */
  product_id?: string | null
  product?: PackageBoundProduct | null
}

export type PackagesResponse = {
  data: PackageListItem[]
  total: number
  limit: number
  offset: number
}

type UsePackagesParams = {
  limit?: number
  offset?: number
  search?: string
  sortField?: string
  sortDir?: 'asc' | 'desc'
  // VERA-790 server-side filters: fill status + product (tag) search.
  status?: 'empty' | 'partial' | 'filled'
  tag?: string
}

type UsePackagesResult = {
  data: PackagesResponse | null
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

export const usePackages = (params: UsePackagesParams = {}, options?: { enabled?: boolean }): UsePackagesResult => {
  const stableParams = useMemo(() => {
    const base: Record<string, any> = {
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }
    if (params.search) base.search = params.search
    if (params.sortField && params.sortDir) {
      base.sort = `${params.sortField}:${params.sortDir}`
    }
    if (params.status) base.status = params.status
    if (params.tag) base.tag = params.tag
    return base
  }, [params.limit, params.offset, params.search, params.sortField, params.sortDir, params.status, params.tag])

  const isEnabled = options?.enabled !== false && stableParams.limit > 0

  const query = useQuery({
    queryKey: ['packages', 'list', stableParams],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/packages',
        params: stableParams,
      })
      return response.data as PackagesResponse
    },
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5,
    // Always refetch when the list mounts (e.g. returning from an encode session that added/moved/
    // unassigned tags) so quantities + fill status reflect changes made elsewhere. (VERA-790)
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
    gcTime: 1000 * 60 * 10,
  })

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

